/**
 * Browser resource-load failures that surface as `Event` promise rejections.
 *
 * An `<img>` (or script/stylesheet) that fails to load fires `error` on the
 * element. Chromium then reports that Event as an unhandled rejection, so
 * Sentry captures `Event { type: "error", target: img }` with no stack. That
 * is a missing asset, not an application exception.
 *
 * `window.onerror` ErrorEvents that wrap a real `Error` are left alone — those
 * are the actual script exceptions we still want.
 */
function isResourceElementTarget(target: EventTarget | null): boolean {
  if (target == null || typeof Element === 'undefined') {
    return false;
  }
  return (
    (typeof HTMLImageElement !== 'undefined' && target instanceof HTMLImageElement) ||
    (typeof HTMLScriptElement !== 'undefined' && target instanceof HTMLScriptElement) ||
    (typeof HTMLLinkElement !== 'undefined' && target instanceof HTMLLinkElement) ||
    (typeof HTMLVideoElement !== 'undefined' && target instanceof HTMLVideoElement) ||
    (typeof HTMLAudioElement !== 'undefined' && target instanceof HTMLAudioElement) ||
    (typeof HTMLSourceElement !== 'undefined' && target instanceof HTMLSourceElement) ||
    (typeof HTMLTrackElement !== 'undefined' && target instanceof HTMLTrackElement)
  );
}

export function isResourceLoadNoise(error: unknown): boolean {
  if (typeof Event === 'undefined' || !(error instanceof Event)) {
    return false;
  }
  if (error.type !== 'error') {
    return false;
  }
  if (
    typeof ErrorEvent !== 'undefined' &&
    error instanceof ErrorEvent &&
    error.error instanceof Error
  ) {
    return false;
  }
  return isResourceElementTarget(error.target);
}
